import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import { fetchSettings } from '../../../store/modules/vatgroup/vatgroupActions';
import useFormSettings from '../../../hooks/useFormSettings';
import CommonSelect from '../../Common/CommonSelect';
import CommonInput from '../../Common/CommonInput';

const VatGroupForm = ({ initialData, onSave, isLoading, onClose }) => {
    const dispatch = useDispatch();
    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('vatgroup', fetchSettings);
    const { types } = settings || {};
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
    const [formData, setFormData] = useState({
        code: '',
        vat_percentage: '',
        description: {},
        xero_sale_tax_code: '',
        xero_purchase_tax_code: '',
        type: null,
        is_active: true,
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                code: initialData.code || '',
                vat_percentage: initialData.vat_percentage || '',
                description: initialData.description || {},
                xero_sale_tax_code: initialData.xero_sale_tax_code || '',
                xero_purchase_tax_code: initialData.xero_purchase_tax_code || '',
                type: initialData.type || null,
                is_active: initialData.is_active,
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleTypeChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            type: selectedOption
        }));

        if (errors.type) {
            setErrors(prev => ({...prev, type: ''}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isSubmitting = isLoading || isSaving;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-end mb-3">
                <LanguageSwitch onLanguageChange={setSelectedLanguage} />
            </div>
            <div className="flex-grow-1">
      
                <div className="row">
                    <div className="col-md-12">
                        <CommonInput
                            name="code"
                            label="VAT Group Code"
                            value={formData.code}
                            onChange={handleChange}
                            placeholder="Enter VAT group code"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-12">
                        <CommonInput
                            name="vat_percentage"
                            label="VAT Percentage"
                            value={formData.vat_percentage}
                            onChange={handleChange}
                            placeholder="Enter VAT percentage"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-12">
                        <CommonSelect
                            name="type"
                            label="Type"
                            value={formData.type}
                            onChange={handleTypeChange}
                            options={types}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select type..."
                        />
                    </div>
                    <div className="col-md-12">
                        <TranslatableTextarea
                            label="Description"
                            name="description"
                            rows={1}
                            value={formData.description}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                    <div className="col-md-6">
                        <CommonInput
                            name="xero_purchase_tax_code"
                            label="Xero Purchase Tax Code"
                            value={formData.xero_purchase_tax_code}
                            onChange={handleChange}
                            placeholder="Enter Xero purchase tax code"
                            disabled={isSubmitting}
                            errors={errors}
                            required={false}
                        />
                    </div>
                    <div className="col-md-6">
                        <CommonInput
                            name="xero_sale_tax_code"
                            label="Xero Sale Tax Code"
                            value={formData.xero_sale_tax_code}
                            onChange={handleChange}
                            placeholder="Enter Xero sale tax code"
                            disabled={isSubmitting}
                            errors={errors}
                            required={false}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    id="is_active"
                                    disabled={isSubmitting}
                                />
                                <label className="form-check-label user-select-none" htmlFor="is_active">
                                    Active
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default VatGroupForm; 